import React, { useState, useRef, useEffect } from "react";
import { Plus, Trash2, Bell, BellOff, Edit, ChevronDown } from "lucide-react";
import { CircularProgress } from "@mui/material";
import AlarmConfig from "./AlarmConfig";
const CustomSelect = ({ value, onChange, options, placeholder, id, disabled, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const selectRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSelect = (optionValue) => {
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
    setHighlightedIndex(-1);
  };
  const handleKeyDown = (e) => {
    if (disabled) return;
    
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }
    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < options.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(options[highlightedIndex].value);
        }
        break;
    }
  };
  const selectedOption = options.find(opt => opt.value === value);
  return (
    <div className="relative" ref={selectRef}>
      <div
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={`w-full p-2.5 text-sm border rounded-md bg-white cursor-pointer flex items-center justify-between transition-all duration-200 focus:outline-none ${
          disabled 
            ? 'text-gray-400 border-gray-200 cursor-not-allowed bg-gray-50' 
            : `text-[#003366] border-gray-200 hover:border-[#003366] ${
                isOpen ? 'border-[#003366] ring-1 ring-[#003366]/20' : ''
              }`
        } ${className || ''}`}
      >
        <span className={selectedOption ? 'text-[#003366]' : 'text-gray-400'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown 
          size={16} 
          className={`text-gray-500 transition-transform duration-200 ${
            isOpen && !disabled ? 'rotate-180' : ''
          }`} 
        />
      </div>
      
      {isOpen && !disabled && (
  <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {options.map((option, index) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 text-sm cursor-pointer transition-colors duration-150 ${
                highlightedIndex === index || value === option.value
                  ? 'bg-[#003366]/10 text-[#003366]' 
                  : 'text-gray-700 hover:bg-gray-50 hover:text-[#003366]'
              }`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default function NamespacesStep({
  currentNamespace, handleNamespaceChange, isLoadingNamespaces, availableNamespaces,
  isLoadingMetrics, availableMetrics, handleMetricToggle, isLoadingResourceNames,
  availableResourceNames, handleResourceNameToggle, addNamespaceToDashboard,
  dashboardData, removeNamespace, collapsedNamespaces, toggleNamespaceCollapse,
  editNamespace, currentAlarmConfigs, updateAlarmConfig
}) {
  const [validationErrors, setValidationErrors] = useState({});
  const getNamespaceOptions = () => {
    if (isLoadingNamespaces) return [];
    return availableNamespaces.map(namespace => ({
      value: namespace,
      label: `${namespace} ${dashboardData.namespaces.some(ns => ns.name === namespace) ? "✓" : ""}`
    }));
  };
  const validateNamespace = () => {
    const errors = {};
    if (!currentNamespace.name) errors.namespace = "Namespace is required";
    if (currentNamespace.resourceNames.length === 0) errors.resourceNames = "At least one resource name is required";
    if (currentNamespace.metrics.length === 0) errors.metrics = "At least one metric is required";
    currentNamespace.metrics.forEach(metric => {
      const alarmConfig = currentAlarmConfigs[metric];
      if (alarmConfig?.enabled) {
        const metricErrors = {};
        if (!alarmConfig.alarms || alarmConfig.alarms.length === 0) {
          metricErrors.alarms = "At least one alarm configuration is required";
        } else {
          alarmConfig.alarms.forEach((alarm, index) => {
            const alarmErrors = {};
            if (!alarm.statistic?.trim()) alarmErrors.statistic = "Statistic is required";
            if (!alarm.comparisonOperator?.trim()) alarmErrors.comparisonOperator = "Comparison operator is required";
            if (!alarm.threshold?.toString().trim()) alarmErrors.threshold = "Threshold is required"; 
            if (Object.keys(alarmErrors).length > 0) {
              if (!metricErrors.alarms) metricErrors.alarms = {};
              metricErrors.alarms[index] = alarmErrors;
            }
          });
        }
        if (Object.keys(metricErrors).length > 0) errors[metric] = metricErrors;
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const cleanupAlarmConfigsForRemovedResources = (removedResourceName) => {
    const metricsWithAlarms = currentNamespace.metrics.filter(metric => 
      currentAlarmConfigs[metric]?.enabled
    );
    metricsWithAlarms.forEach(metric => {
      const alarmConfig = currentAlarmConfigs[metric];
      if (alarmConfig?.alarms && Array.isArray(alarmConfig.alarms)) {
        let hasChanges = false;
        const updatedAlarms = [];
        alarmConfig.alarms.forEach(alarm => {
          if (alarm.resourceNames && Array.isArray(alarm.resourceNames)) {
            const filteredResourceNames = alarm.resourceNames.filter(
              resourceName => resourceName !== removedResourceName
            );
            if (filteredResourceNames.length > 0) {
              updatedAlarms.push({ ...alarm, resourceNames: filteredResourceNames });
              if (filteredResourceNames.length !== alarm.resourceNames.length) hasChanges = true;
            } else {
              hasChanges = true;
            }
          } else if (alarm.resourceName === removedResourceName) {
            hasChanges = true;
          } else {
            updatedAlarms.push(alarm);
          }
        });
        if (hasChanges) {
          if (updatedAlarms.length === 0) {
            updateAlarmConfig(metric, "enabled", false);
            updateAlarmConfig(metric, "alarms", []);
          } else {
            updateAlarmConfig(metric, "alarms", updatedAlarms);
          }
        }
      }
    });
  };
  const clearValidationError = (path) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[path];
      return newErrors;
    });
  };
  const handleResourceNameToggleWithValidation = (resourceName) => {
    const wasSelected = currentNamespace.resourceNames.includes(resourceName);
    handleResourceNameToggle(resourceName);
    
    if (wasSelected) cleanupAlarmConfigsForRemovedResources(resourceName);
    if (validationErrors.resourceNames) clearValidationError('resourceNames');
  };
  const handleAddNamespaceWithValidation = () => {
    if (validateNamespace()) {
      addNamespaceToDashboard();
      setValidationErrors({});
    }
  };
  const handleNamespaceChangeWithValidation = (e) => {
    handleNamespaceChange(e);
    if (validationErrors.namespace) clearValidationError('namespace');
  };
  const handleMetricToggleWithValidation = (metric) => {
    handleMetricToggle(metric);
    if (validationErrors.metrics) clearValidationError('metrics');
  };
  const clearAlarmValidationError = (metric, alarmIndex, field) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors[metric]?.alarms?.[alarmIndex]) {
        delete newErrors[metric].alarms[alarmIndex][field];
        if (Object.keys(newErrors[metric].alarms[alarmIndex]).length === 0) {
          delete newErrors[metric].alarms[alarmIndex];
        }
        if (Object.keys(newErrors[metric].alarms).length === 0) {
          delete newErrors[metric].alarms;
        }
        if (Object.keys(newErrors[metric]).length === 0) {
          delete newErrors[metric];
        }
      }
      return newErrors;
    });
  };
  const updateAlarmConfigWithValidation = (metric, field, value, alarmIndex) => {
    updateAlarmConfig(metric, field, value, alarmIndex);
    if (field === 'alarms' && Array.isArray(value) && value.length > 0) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[metric]?.alarms === "At least one alarm configuration is required") {
          delete newErrors[metric].alarms;
          if (Object.keys(newErrors[metric]).length === 0) delete newErrors[metric];
        }
        return newErrors;
      });
    }
  };
  const hasAlarms = (namespace) => {
    return namespace.metrics.some(metric =>
      namespace.alarmConfigs?.[metric]?.enabled
    );
  };
  const toggleAlarmEnabled = (metric) => {
    updateAlarmConfig(metric, "enabled", !(currentAlarmConfigs[metric]?.enabled), 0);
  };
  const addAlarmToMetric = (metric) => {
    const currentConfig = currentAlarmConfigs[metric] || { enabled: true, alarms: [] };
    if (!currentConfig.alarms || currentConfig.alarms.length === 0) {
      currentConfig.alarms = [{
        statistic: currentConfig.statistic || "Average",
        comparisonOperator: currentConfig.comparisonOperator || "GreaterThanOrEqualToThreshold",
        threshold: currentConfig.threshold || "0",
        resourceNames: currentConfig.resourceName ? [currentConfig.resourceName] : [],
        alarmName: ""
      }];
    }
    currentConfig.alarms.push({
      statistic: "Average",
      comparisonOperator: "GreaterThanOrEqualToThreshold",
      threshold: "0",
      resourceNames: [],
      alarmName: ""
    });
    updateAlarmConfigWithValidation(metric, "alarms", currentConfig.alarms);
  };
  const getAlarmDisplayText = (alarm) => {
    const operatorSymbols = {
      "GreaterThanOrEqualToThreshold": "≥",
      "GreaterThanThreshold": ">",
      "LessThanThreshold": "<",
      "LessThanOrEqualToThreshold": "≤"
    };
    let resourceText = '';
    if (alarm.resourceNames?.length > 0) {
      resourceText = alarm.resourceNames.length === 1 
        ? ` (${alarm.resourceNames[0]})` 
        : ` (${alarm.resourceNames.length} resources)`;
    } else if (alarm.resourceName) {
      resourceText = ` (${alarm.resourceName})`;
    } else {
      resourceText = ' (All Resources)';
    }
    return `${alarm.statistic} ${operatorSymbols[alarm.comparisonOperator] || alarm.comparisonOperator} ${alarm.threshold}${resourceText}`;
  };
  const getIndividualAlarms = (alarmConfig, metric) => {
    if (!alarmConfig?.alarms) return [];
    const individualAlarms = [];
    alarmConfig.alarms.forEach((alarm) => {
      if (alarm.resourceNames?.length > 0) {
        alarm.resourceNames.forEach((resourceName) => {
          individualAlarms.push({
            ...alarm,
            resourceNames: [resourceName],
            alarmName: `dev-us-cloudlens-${metric}-${resourceName}`
          });
        });
      } else {
        individualAlarms.push({
          ...alarm,
          resourceNames: [],
          alarmName: `dev-us-cloudlens-${alarm.metricName || 'all'}`
        });
      }
    });
    return individualAlarms;
  };
  const ErrorMessage = ({ error }) => error && (
    <p className="mt-1 text-sm text-red-600">{error}</p>
  );
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-24">
      <CircularProgress size={20} sx={{ color: "#003366" }} />
    </div>
  );
  return (
    <div className="space-y-6">
      <style>
        {`
          .custom-checkbox {
            appearance: none;
            width: 16px;
            height: 16px;
            border: 2px solid #d1d5db;
            border-radius: 3px;
            background-color: white;
            cursor: pointer;
            position: relative;
            transition: all 0.2s ease;
          }
          
          .custom-checkbox:checked {
            background-color: #003366;
            border-color: #003366;
          }
          
          .custom-checkbox:checked::after {
            content: '';
            position: absolute;
            left: 4px;
            top: 1px;
            width: 4px;
            height: 8px;
            border: solid white;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
          }
          
          .custom-checkbox:focus {
            outline: none;
            box-shadow: 0 0 0 2px rgba(0, 51, 102, 0.2);
          }
          
          .custom-checkbox:hover:not(:checked) {
            border-color: #003366;
            background-color: rgba(0, 51, 102, 0.05);
          }
        `}
      </style>  
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" id="namespace-form">
        <h3 className="text-lg font-bold text-[#003366] mb-6">
          Add Namespace and Metrics
        </h3>
        <div className="space-y-5">
          <div>
            <label htmlFor="namespace" className="block text-sm font-medium text-[#003366] mb-2">
              Namespace
            </label>
            <CustomSelect
              id="namespace"
              value={currentNamespace.name}
              onChange={handleNamespaceChangeWithValidation}
              options={getNamespaceOptions()}
              placeholder={isLoadingNamespaces ? "Loading namespaces..." : "Select Namespace"}
              disabled={isLoadingNamespaces}
              className={validationErrors.namespace ? 'border-red-500' : ''}
            />
            <ErrorMessage error={validationErrors.namespace} />
          </div>
          {currentNamespace.name && (
            <>
              <div>
                <label className="block text-sm font-medium text-[#003366] mb-2">
                  Resource Names
                </label>
                <div className={`border rounded-md p-3 bg-white max-h-48 overflow-y-auto ${
                  validationErrors.resourceNames ? 'border-red-500' : 'border-gray-200'
                }`}>
                  {isLoadingResourceNames ? (
                    <LoadingSpinner />
                  ) : availableResourceNames.filter(name => name?.trim()).length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {availableResourceNames
                        .filter(name => name?.trim())
                        .map((resourceName) => (
                          <div key={resourceName} className="flex items-center py-1">
                            <input
                              type="checkbox"
                              id={`resource-${resourceName}`}
                              checked={currentNamespace.resourceNames.includes(resourceName)}
                              onChange={() => handleResourceNameToggleWithValidation(resourceName)}
                              className="custom-checkbox"
                            />
                            <label
                              htmlFor={`resource-${resourceName}`}
                              className={`ml-2 text-xs cursor-pointer hover:text-[#003366] truncate max-w-xs ${
                                currentNamespace.resourceNames.includes(resourceName)
                                  ? "text-[#003366] font-medium"
                                  : "text-gray-700"
                              }`}
                              title={resourceName}
                            >
                              {resourceName}
                            </label>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 py-2">No resource names available for this namespace</p>
                  )}
                </div>
                <ErrorMessage error={validationErrors.resourceNames} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#003366] mb-2">
                  Available Metrics
                </label>
                <div className={`border rounded-md bg-white overflow-visible ${
  validationErrors.metrics ? 'border-red-500' : 'border-gray-200'
}`}>
                  {isLoadingMetrics ? (
                    <LoadingSpinner />
                  ) : availableMetrics.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {availableMetrics.map((metric) => {
                        const isSelected = currentNamespace.metrics.includes(metric);
                        const alarmConfig = currentAlarmConfigs[metric] || {
                          enabled: false,
                          threshold: "0",
                          statistic: "Average",
                          comparisonOperator: "GreaterThanOrEqualToThreshold",
                          alarms: []
                        };
                        const metricValidationErrors = validationErrors[metric] || {};
                        return (
                          <div key={metric} className="p-2.5 hover:bg-gray-50">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`metric-${metric}`}
                                checked={isSelected}
                                onChange={() => handleMetricToggleWithValidation(metric)}
                                className="custom-checkbox"
                              />
                              <label
                                htmlFor={`metric-${metric}`}
                                className={`ml-2 text-xs cursor-pointer hover:text-[#003366] ${
                                  isSelected ? "text-[#003366] font-medium" : "text-gray-700"
                                }`}
                              >
                                {metric}
                              </label>
                              {isSelected && (
  <button
    onClick={() => toggleAlarmEnabled(metric)}
    className={`ml-auto p-1.5 rounded transition-colors ${
      alarmConfig.enabled
        ? "bg-[#003366]/10 text-[#003366] hover:bg-[#003366]/20"
        : "bg-gray-50 text-gray-400 hover:bg-gray-100"
    }`}
    title={alarmConfig.enabled ? "Edit alarm configuration" : "Add alarm"}
  >
    {alarmConfig.enabled ? (
      <span className="flex items-center text-xs">
        <Bell size={14} className="mr-1" /> Disable Alarm
      </span>
    ) : (
      <span className="flex items-center text-xs">
        <BellOff size={14} className="mr-1" /> Enable Alarm
      </span>
    )}
  </button>
)}
                            </div>
                            {metricValidationErrors.alarms && typeof metricValidationErrors.alarms === 'string' && (
                              <p className="mt-1 ml-6 text-sm text-red-600">{metricValidationErrors.alarms}</p>
                            )}
                            {isSelected && alarmConfig.enabled && (
                              <AlarmConfig
                                alarmConfig={alarmConfig}
                                updateAlarmConfig={updateAlarmConfigWithValidation}
                                metricName={metric}
                                resourceNames={currentNamespace.resourceNames}
                                onAddAlarm={() => addAlarmToMetric(metric)}
                                validationErrors={metricValidationErrors}
                                clearValidationError={clearAlarmValidationError}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 p-4">No metrics available for this namespace</p>
                  )}
                </div>
                <ErrorMessage error={validationErrors.metrics} />
              </div>
            </>
          )}
          <div className="flex justify-end mt-4">
            <button
              onClick={handleAddNamespaceWithValidation}
              disabled={!currentNamespace.name || currentNamespace.metrics.length === 0}
              className="flex items-center px-4 py-2 bg-[#003366] text-white rounded-md hover:bg-[#004488] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <Plus size={16} className="mr-2" />
              {dashboardData.namespaces.some(ns => ns.name === currentNamespace.name)
                ? "Update Namespace"
                : "Add Namespace"}
            </button>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-[#003366] mb-4">
          Selected Namespaces
        </h3>
        {dashboardData.namespaces.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-gray-200 rounded-md">
            <p className="text-gray-500">No namespaces added yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {dashboardData.namespaces.map((namespace, index) => (
              <div key={index} className="bg-white p-4 rounded-md border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <h4 className="font-medium text-gray-800 text-base">
                      {namespace.name}
                    </h4>
                    {hasAlarms(namespace) && (
                      <span className="ml-2 text-[#003366]" title="Has alarm(s) configured">
                        <Bell size={14} />
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <button
                      onClick={() => editNamespace(namespace)}
                      className="p-1 bg-[#003366]/5 text-[#003366] hover:bg-[#003366]/10 rounded-md"
                      title="Edit namespace"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => removeNamespace(index)}
                      className="p-1 bg-red-50 text-red-500 hover:bg-red-100 rounded-md"
                      title="Remove namespace"
                    >
                      <Trash2 size={16} />
                    </button>
                    <button
                      onClick={() => toggleNamespaceCollapse(index)}
                      className="p-1 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      aria-label={collapsedNamespaces.includes(index) ? "Expand" : "Collapse"}
                    >
                      {collapsedNamespaces.includes(index) ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-800">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-800">
                          <polyline points="18 15 12 9 6 15"></polyline>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                {!collapsedNamespaces.includes(index) && (
                  <div className="mt-3 space-y-4">
                    {namespace.resourceNames?.length > 0 && (
                      <div className="bg-white p-3 rounded-md border border-gray-100">
                        <h5 className="font-medium text-[#003366] text-sm mb-2">Resource Names:</h5>
                        <div className="flex flex-wrap gap-1.5">
                          {namespace.resourceNames.map((resourceName) => (
                            <span
                              key={resourceName}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#003366]/10 text-[#003366]"
                              title={resourceName}
                            >
                              <span className="truncate max-w-32">{resourceName}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="bg-white p-3 rounded-md border border-gray-100">
                      <h5 className="font-medium text-[#003366] text-sm mb-2">Metrics & Alarms:</h5>
                      <div className="space-y-3">
                        {namespace.metrics.map((metric) => {
                          const alarmConfig = namespace.alarmConfigs?.[metric];
                          const hasAlarm = alarmConfig?.enabled;
                          return (
                            <div key={metric} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                              <div className="flex items-center justify-between">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#003366]/10 text-[#003366]">
                                  {metric}
                                </span>
                                {hasAlarm && (
                                  <span className="text-[#003366]" title="Alarm configured">
                                    <Bell size={14} />
                                  </span>
                                )}
                              </div>
                              {hasAlarm && (
                                <div className="mt-2 pl-1.5">
                                  <div className="text-xs text-gray-500 mb-1">Individual Alarms:</div>
                                  {alarmConfig.alarms?.length > 0 ? (
                                    <div className="space-y-1">
                                      {getIndividualAlarms(alarmConfig, metric).map((alarm, alarmIdx) => (
                                        <div key={alarmIdx} className="bg-[#003366]/5 px-2 py-1 rounded text-xs">
                                          <span>{getAlarmDisplayText(alarm)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="bg-[#003366]/5 px-2 py-1 rounded text-xs">
                                      {getAlarmDisplayText({
                                        statistic: alarmConfig.statistic || "Average",
                                        comparisonOperator: alarmConfig.comparisonOperator || "GreaterThanOrEqualToThreshold",
                                        threshold: alarmConfig.threshold || "0",
                                        resourceName: alarmConfig.resourceName || ""
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}